import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Image,
  useColorModeValue,
  Avatar,
  Badge,
  Divider,
  Icon,
} from '@chakra-ui/react';

interface TeamOverviewTabProps {
  team: {
    id?: string;
    name?: string;
    description?: string;
    team_type?: string;
    status?: string;
    created_at?: string;
    avatar_url?: string;
    banner_url?: string;
  };
  counters: {
    members_count: number;
    invitations_count: number;
    roles_count: number;
    membership_requests_count: number;
    role_requests_count: number;
    ownership_requests_count: number;
    contacts_count: number;
    audit_logs_count: number;
  };
}

export const TeamOverviewTab: React.FC<TeamOverviewTabProps> = ({
  team,
  counters,
}) => {
  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const gradientBg = useColorModeValue(
    'linear(to-r, blue.400, purple.500)',
    'linear(to-r, blue.600, purple.700)'
  );

  // Icons for stats
  const MembersIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-1c0-2.66 5.33-4 8-4s8 1.34 8 4v1H4zM12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
    </svg>
  );

  const InvitationsIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
  );

  const RolesIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.4C14.8,12.8 13.4,14.4 12,14.4C10.6,14.4 9.2,12.8 9.2,11.4V10C9.2,8.6 10.6,7 12,7Z" />
    </svg>
  );

  return (
    <VStack spacing={6} align="stretch">
      {/* Welcome Post Card */}
      <Card bg={cardBg} borderWidth={1} borderColor={borderColor} shadow="lg" overflow="hidden">
        {/* Post Header */}
        <Box
          bgGradient={gradientBg}
          p={6}
          color="white"
          position="relative"
        >
          <HStack spacing={4} align="start">
            <Avatar
              size="lg"
              name={team.name}
              src={team.avatar_url}
              bg="whiteAlpha.300"
              border="3px solid"
              borderColor="whiteAlpha.500"
            />
            <VStack align="start" spacing={1} flex={1}>
              <Text fontSize="xl" fontWeight="bold">
                Welcome to {team.name || 'Team'}
              </Text>
              <Text fontSize="sm" opacity={0.9}>
                {team.created_at ? new Date(team.created_at).toLocaleDateString() : 'Unknown date'} • {team.team_type || 'Unknown type'}
              </Text>
            </VStack>
            <Badge colorScheme="whiteAlpha" variant="solid">
              {team.status || 'Unknown'}
            </Badge>
          </HStack>
        </Box>

        {/* Post Content */}
        <CardBody p={6}>
          <VStack spacing={4} align="stretch">
            {/* Welcome Image */}
            <Box borderRadius="lg" overflow="hidden">
              <Image
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                alt="Team collaboration"
                w="full"
                h="200px"
                objectFit="cover"
                fallback={
                  <Box
                    w="full"
                    h="200px"
                    bg={useColorModeValue('gray.100', 'gray.700')}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color={textColor}>Welcome Image</Text>
                  </Box>
                }
              />
            </Box>

            {/* Welcome Text */}
            <VStack spacing={3} align="start">
              <Text fontSize="lg" fontWeight="semibold">
                Welcome to our team!
              </Text>
              <Text color={textColor} lineHeight="tall">
                {team.description || `Welcome to ${team.name || 'the team'}! This is your team's main page where you can see recent activity and quickly navigate to different sections.`}
              </Text>
              <Text color={textColor} fontSize="sm">
                Team created on {team.created_at ? new Date(team.created_at).toLocaleDateString() : 'unknown date'} • {counters.members_count} members
              </Text>
            </VStack>

            {/* Quick Stats */}
            <HStack spacing={4} pt={2}>
              <HStack spacing={2}>
                <Icon as={MembersIcon} color={textColor} />
                <Text fontSize="sm" color={textColor}>
                  {counters.members_count} Members
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={InvitationsIcon} color={textColor} />
                <Text fontSize="sm" color={textColor}>
                  {counters.invitations_count} Invitations
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={RolesIcon} color={textColor} />
                <Text fontSize="sm" color={textColor}>
                  {counters.roles_count} Roles
                </Text>
              </HStack>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      <Divider />

      {/* Recent Activity Placeholder */}
      <VStack spacing={4} align="stretch">
        <Text fontSize="xl" fontWeight="bold">
          Recent Activity
        </Text>
        
        <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
          <CardBody p={8} textAlign="center">
            <VStack spacing={3}>
              <Box
                p={4}
                borderRadius="full"
                bg={useColorModeValue('gray.100', 'gray.700')}
              >
                <Icon
                  viewBox="0 0 24 24"
                  boxSize={8}
                  color={textColor}
                >
                  <path
                    fill="currentColor"
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  />
                </Icon>
              </Box>
              <Text fontSize="lg" fontWeight="medium" color={textColor}>
                Activity Feed Coming Soon
              </Text>
              <Text fontSize="sm" color={textColor}>
                Here you'll see recent team activity, posts, and updates
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </VStack>
  );
}; 